import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
} from '@angular/core';
import {
    ContainerFragment,
    EquipmentFragment,
    SlotFragment,
    ZoneLocationIdentifierViewModel,
} from '@control-tower/platform-wms';
import { ChipStatus, ConnectedChip, SharedUiModule } from '@pf/shared-ui';
import { ContainerFacade, EquipmentFacade } from '@pf/platform-wms/domain';
import { firstValueFrom } from 'rxjs';
import { asArraySafe, asNumberSafe } from '@pf/shared-utility';
import { orderBy, uniqBy } from 'lodash-es';
import { AsyncPipe } from '@angular/common';
import { ProfileProvider } from '@pf/shared-common';
import { UserAccountType } from '@pf/shared-services';

@Component({
    selector: 'cpw-item-location-path',
    imports: [SharedUiModule, AsyncPipe],
    templateUrl: './cpw-item-location-path.component.html',
    styleUrl: './cpw-item-location-path.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CpwItemLocationPathComponent {
    private readonly equipmentFacade = inject(EquipmentFacade);
    private readonly containerFacade = inject(ContainerFacade);
    private readonly profileProvider = inject(ProfileProvider);

    private isAdmin =
        this.profileProvider.profile.userGroupType?.toLowerCase() ===
        UserAccountType.Administrator;

    chipStatus = input<ChipStatus>();

    segmented = input<boolean>(true);
    slot = input<Pick<SlotFragment, 'barcode' | 'zone'> | undefined | null>(
        null
    );
    container = input<ContainerFragment | undefined | null>(null);
    showLast = input<number | undefined>(undefined);

    locationChips = computed(async () => {
        const chips = await this.calculateLocationChips();
        const limit = this.showLast();
        if (limit !== undefined && limit > 0 && chips.length > limit) {
            return chips.slice(-limit);
        }
        return chips;
    });
    location = computed(async () => {
        return asArraySafe(await this.locationChips())
            .map(chip => chip.label)
            .join(' > ');
    });

    /**
     * Structured location for the table (non-segmented) view: line 1 is the zone
     * path with Aisle/Bay/Level (location level >= 3) marked strong and Business
     * Unit/Zone (levels 1-2) muted; line 2 is the specific holder — the slot
     * ("Slot N") or the equipment/container (e.g. "Forklift FL0007 · Pallet
     * PL0351") — so it reads consistently regardless of location kind.
     */
    emphasizedLocation = computed(async () => {
        const slot = this.slot();
        if (slot) {
            return {
                path: this.zonePath(slot.zone?.locationIdentifiers),
                second: this.slotLabel(slot.barcode),
            };
        }
        return await this.containerEmphasized(this.container());
    });

    private zonePath(
        identifiers: ZoneLocationIdentifierViewModel[] | null | undefined
    ) {
        return uniqBy(
            orderBy(asArraySafe(identifiers), ['level'], ['asc']),
            identifier => asNumberSafe(identifier.level)
        ).map(identifier => ({
            label: identifier.name,
            strong: asNumberSafe(identifier.level) >= 3,
        }));
    }

    private slotLabel(barcode?: string | null): string | null {
        const slotNumber = barcode?.split('-')[1];
        return slotNumber ? `Slot ${slotNumber}` : null;
    }

    private async containerEmphasized(
        container?: ContainerFragment | null
    ): Promise<{
        path: { label: string; strong: boolean }[];
        second: string | null;
    }> {
        if (!container) {
            return { path: [], second: null };
        }
        let path: { label: string; strong: boolean }[] = [];
        const second: string[] = [];

        if (container.slot) {
            path = this.zonePath(container.slot.zone?.locationIdentifiers);
            const slotText = this.slotLabel(container.slot.barcode);
            if (slotText) {
                second.push(slotText);
            }
            // Matches the original logic: a slot-only container has no LPN line.
            if (!container.containerTypeId) {
                return { path, second: second.join(' · ') || null };
            }
        } else if (container.equipment) {
            const equipment = await firstValueFrom(
                this.equipmentFacade.getById$(container.equipment.id as string)
            );
            path = this.zonePath(equipment?.zone?.locationIdentifiers);
            const equipmentLabel = `${
                equipment?.equipmentType
                    ? equipment.equipmentType.name + ' '
                    : ''
            }${equipment?.barcode || ''}`.trim();
            if (equipmentLabel) {
                second.push(equipmentLabel);
            }
        }

        const fullContainer = await firstValueFrom(
            this.containerFacade.getById$(container.id as string)
        );
        if (fullContainer?.lpn) {
            second.push(
                `${fullContainer.containerType?.name ? fullContainer.containerType.name + ' ' : ''}${fullContainer.lpn}`
            );
        }

        return { path, second: second.join(' · ') || null };
    }

    private async calculateLocationChips(): Promise<ConnectedChip[]> {
        if (this.slot()) {
            return this.getSlotLocationChips(this.slot());
        }
        return await this.getContainerLocationChips(this.container());
    }
    private async getContainerLocationChips(
        container?: ContainerFragment | null
    ) {
        const chips: ConnectedChip[] = [];

        if (!container) {
            return chips;
        }

        if (container.slot) {
            const slotChips = this.getSlotLocationChips(container.slot);
            chips.push(...slotChips);

            if (!container.containerTypeId) {
                return chips;
            }
        } else if (container.equipment) {
            const equipmentChips = await this.getEquipmentLocationChips(
                container.equipment
            );
            chips.push(...equipmentChips);
        }

        const fullContainer = await firstValueFrom(
            this.containerFacade.getById$(container.id as string)
        );

        if (fullContainer?.lpn) {
            chips.push({
                label: `${fullContainer.containerType?.name ? fullContainer.containerType.name + ' ' : ''}${fullContainer.lpn}`,
            });
        }

        return chips;
    }

    copyLocationBarcode() {
        if (!this.isAdmin) return;
        const barcode = this.container()?.slot?.barcode || this.slot()?.barcode;
        if (barcode) {
            navigator.clipboard.writeText(barcode);
        }
    }

    private async getEquipmentLocationChips(
        equipmentFragment?: EquipmentFragment | null
    ) {
        const chips: ConnectedChip[] = [];

        if (!equipmentFragment) {
            return chips;
        }

        const equipment = await firstValueFrom(
            this.equipmentFacade.getById$(equipmentFragment.id as string)
        );

        if (!equipment) {
            return chips;
        }

        if (equipment?.zone?.locationIdentifiers) {
            const zoneChips = this.getZoneLocationChips(
                equipment.zone.locationIdentifiers
            );
            chips.push(...zoneChips);
        }
        chips.push({
            label: `${equipment.equipmentType ? equipment.equipmentType.name + ' ' : ''}${equipment.barcode || ''}`,
        });

        return chips;
    }

    private getSlotLocationChips(
        slot?: Pick<SlotFragment, 'barcode' | 'zone'> | null
    ) {
        const chips: ConnectedChip[] = [];

        if (!slot) {
            return chips;
        }

        const slotNumber =
            asNumberSafe(slot.barcode?.split('-')[1]) || slot.barcode;

        if (slotNumber) {
            chips.unshift({
                label: slotNumber.toString(),
            });
        }

        if (!slot.zone) {
            return chips;
        }

        const zoneChips = this.getZoneLocationChips(
            slot.zone.locationIdentifiers
        );
        chips.unshift(...zoneChips);

        return chips;
    }
    private getZoneLocationChips(
        zoneLocationIdentifiers:
            | ZoneLocationIdentifierViewModel[]
            | null
            | undefined
    ) {
        return uniqBy(
            orderBy(asArraySafe(zoneLocationIdentifiers), ['level'], ['asc']),
            identifier => asNumberSafe(identifier.level)
        ).map(identifier => ({
            label: identifier.name,
        }));
    }
}
